import React from 'react'
import { GetServerSideProps, NextPage } from 'next'
import { readToken } from '../../lib/sanity.api'

import {
  getClient,
  getCountryBySlug,
  getGetAllRegionsInCountry,
  getGetAllStatesInCountry,
  getGetAllProductsInCountry,
  getGetAllCitiesInCountry,
} from '../../lib/sanity.client'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { PageBanner } from '../../components/PageBanner'
import PageHead from '../../components/shared/PageHead'
import { Description } from '../../components/shared/Description'
import { FAQ } from '../../components/shared/FAQ'
import TileSlider from '../../components/structure/TileSlider'
import { getAlternates } from '../../lib/helper/helper'
import CustomGrid from '../../components/structure/CustomGrid'

interface CountryProps {
  slug: string
  country: Country
  settings: any
}

const CountryPage: NextPage<CountryProps> = ({ country }: CountryProps) => {
  const {
    name,
    coverImage,
    nameDescription,
    h1,
    meta_title,
    meta_description,
    _id,
    description_1,
    description_2,
    description_3,
    faq,
    reviews,
    number_activities,
    number_locations,
    product_link,
  } = country

  return (
    <>
      <PageHead
        meta_title={meta_title}
        meta_description={meta_description}
        alternates={getAlternates(country)}
        coverImage={coverImage}
        wildcards={{
          number_activities,
          number_locations,
        }}
        item={country}
        faqs={faq}
      />
      <PageBanner
        coverImage={coverImage}
        name={name}
        nameDescription={nameDescription}
        h1={h1}
        wildcards={{
          number_activities,
          number_locations,
        }}
        product_link={product_link}
      />
      <CustomGrid loadWith={getGetAllCitiesInCountry(_id)} type="location" />
      <TileSlider
        labelKey={'top-products-in-location'}
        location={name}
        loadWith={getGetAllProductsInCountry(_id)}
        type={'product'}
      />
      <TileSlider
        labelKey={'top-regions-in-location'}
        location={name}
        loadWith={getGetAllRegionsInCountry(_id)}
      />
      <TileSlider
        labelKey={'states'}
        loadWith={getGetAllStatesInCountry(_id)}
      />
      {!!faq?.length && faq !== null && faq.length > 0 && (
        <FAQ faqs={faq} nameEntity={name} />
      )}
      {description_1 && !!description_1.length && (
        <Description
          description={description_1}
          withBorder
          nameLocation={name}
          {...reviews}
        />
      )}
      {description_2 && !!description_2.length && (
        <Description description={description_2} withBorder />
      )}
      {description_3 && !!description_3.length && (
        <Description description={description_3} withBorder />
      )}
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params, preview, locale } = context
  const { country: countrySlug } = params
  const client = getClient(preview ? { token: readToken } : undefined)

  const [country, translations] = await Promise.all([
    getCountryBySlug(locale as AppLocale, client, countrySlug as string),
    serverSideTranslations(locale, ['common']),
  ])

  if (!country) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...translations,
      slug: countrySlug,
      country,
    },
  }
}

export default CountryPage
